import { NavLink } from 'react-router-dom'
import { useState } from 'react'
import { version as appVersion } from '../../../package.json'
import {
  HomeIcon,
  CubeIcon,
  ShoppingCartIcon,
  UsersIcon,
  TruckIcon,
  BanknotesIcon,
  ChartBarIcon,
  CpuChipIcon,
  BellIcon,
  Cog6ToothIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  BuildingOfficeIcon,
  SparklesIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline'

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  {
    name: 'Products',
    icon: CubeIcon,
    children: [
      { name: 'All Products', href: '/products' },
      { name: 'Stock Prices', href: '/stock-prices' },
      { name: 'Bundles', href: '/bundles' },
      { name: 'Instances', href: '/bundles/instances' },
      { name: 'Inventory', href: '/inventory' },
    ],
  },
  { name: 'Suppliers', href: '/suppliers', icon: BuildingOfficeIcon },
  { name: 'Purchases', href: '/purchases', icon: ShoppingCartIcon },
  { name: 'Quotations', href: '/quotations', icon: BanknotesIcon },
  {
    name: 'Sales',
    icon: ShoppingCartIcon,
    children: [
      { name: 'All Sales', href: '/sales' },
      { name: 'POS', href: '/pos' },
      { name: 'Reversals', href: '/sale-reversals' },
    ],
  },
  { name: 'Customers', href: '/customers', icon: UsersIcon },
  { name: 'Consignments', href: '/consignments', icon: TruckIcon },
  {
    name: 'Financial',
    icon: BanknotesIcon,
    children: [
      { name: 'Payments', href: '/payments' },
      { name: 'Invoices', href: '/invoices' },
      { name: 'Debtors Report', href: '/reports/debtors' },
      { name: 'Statements', href: '/reports/statements' },
    ],
  },
  { name: 'Reports', href: '/reports', icon: ChartBarIcon },
  { name: 'Reviews', href: '/reviews/google', icon: ChatBubbleLeftRightIcon },
  { name: 'Reports Assistant', href: '/ai-assistant', icon: CpuChipIcon },
  { name: 'AI Reports', href: '/ai-reports', icon: SparklesIcon },
]

const bottomNavigation = [
  { name: 'Notifications', href: '/notifications', icon: BellIcon },
  {
    name: 'Settings',
    icon: Cog6ToothIcon,
    children: [
      { name: 'General', href: '/settings' },
      { name: 'Brands', href: '/settings/brands' },
      { name: 'Categories', href: '/settings/categories' },
    ],
  },
]

export const Sidebar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [expandedItems, setExpandedItems] = useState<string[]>([])

  const toggleExpanded = (name: string) => {
    setExpandedItems((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    )
  }

  return (
    <>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-900/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile menu button */}
      <div className="fixed top-0 left-0 right-0 z-30 flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 lg:hidden">
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="w-6 h-6" />
          </button>
          <span className="text-xl font-bold text-primary-600">Aperture</span>
        </div>
      </div>

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 border-r border-slate-700 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-auto ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="Aperture Logo"
              className="h-10 w-auto object-contain"
            />
            <span className="text-xl font-bold text-white"></span>
          </div>
          <button
            type="button"
            className="lg:hidden -m-2.5 p-2.5 text-slate-300 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex flex-col flex-1 overflow-y-auto py-4 px-3 h-[calc(100vh-64px)]">
          <ul className="space-y-1">
            {navigation.map((item) => (
              <li key={item.name}>
                {item.children ? (
                  <div>
                    <button
                      onClick={() => toggleExpanded(item.name)}
                      className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-slate-300 rounded-lg hover:bg-slate-700/50 hover:text-white transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <item.icon className="w-5 h-5" />
                        {item.name}
                      </div>
                      <ChevronDownIcon
                        className={`w-4 h-4 transition-transform ${expandedItems.includes(item.name) ? 'rotate-180' : ''
                          }`}
                      />
                    </button>
                    {expandedItems.includes(item.name) && (
                      <ul className="mt-1 ml-8 space-y-1">
                        {item.children.map((child) => (
                          <li key={child.name}>
                            <NavLink
                              to={child.href}
                              onClick={() => setSidebarOpen(false)}
                              className={({ isActive }) =>
                                `block px-3 py-2 text-sm font-medium rounded-lg ${isActive
                                  ? 'bg-primary-600/20 text-primary-400 border border-primary-500/30'
                                  : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                                }`
                              }
                            >
                              {child.name}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <NavLink
                    to={item.href!}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive
                        ? 'bg-primary-600/20 text-primary-400 border border-primary-500/30'
                        : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                      }`
                    }
                  >
                    <item.icon className="w-5 h-5" />
                    {item.name}
                  </NavLink>
                )}
              </li>
            ))}
          </ul>

          {/* Bottom navigation */}
          <div className="mt-auto pt-4 border-t border-slate-700">
            <ul className="space-y-1">
              {bottomNavigation.map((item) => (
                <li key={item.name}>
                  {item.children ? (
                    <div>
                      <button
                        onClick={() => toggleExpanded(item.name)}
                        className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-slate-300 rounded-lg hover:bg-slate-700/50 hover:text-white transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <item.icon className="w-5 h-5" />
                          {item.name}
                        </div>
                        <ChevronDownIcon
                          className={`w-4 h-4 transition-transform ${expandedItems.includes(item.name) ? 'rotate-180' : ''
                            }`}
                        />
                      </button>
                      {expandedItems.includes(item.name) && (
                        <ul className="mt-1 ml-8 space-y-1">
                          {item.children.map((child) => (
                            <li key={child.name}>
                              <NavLink
                                to={child.href}
                                onClick={() => setSidebarOpen(false)}
                                className={({ isActive }) =>
                                  `block px-3 py-2 text-sm font-medium rounded-lg ${isActive
                                    ? 'bg-primary-600/20 text-primary-400 border border-primary-500/30'
                                    : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                                  }`
                                }
                              >
                                {child.name}
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : (
                    <NavLink
                      to={item.href!}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive
                          ? 'bg-primary-600/20 text-primary-400 border border-primary-500/30'
                          : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                        }`
                      }
                    >
                      <item.icon className="w-5 h-5" />
                      {item.name}
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Version */}
          <div className="pt-3 mt-3 border-t border-slate-700 px-3">
            <p className="text-xs text-slate-500">SnapShot v{appVersion}</p>
          </div>
        </nav>
      </div>
    </>
  )
}
